import { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import { X, File, FilePdf, FileImage, FileXls, FileDoc, Download, Warning, Spinner, Eye, Camera, Upload, CheckCircle } from "@phosphor-icons/react";
import { fetchPartFiles, resolveDriveFile, uploadPartFile } from "@/lib/api";
import { extractErr } from "@/lib/errors";
import { toast } from "sonner";

const IMG_EXT = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
const PDF_EXT = ["pdf"];

// Per-session thumbnail cache so hovering back over the same file is instant.
const THUMB_CACHE = new Map(); // name -> thumbnail_url
let HOVER_DEBOUNCE = null;

// ─── Download filename helpers ──────────────────────────────────
// Strip characters that are invalid in Windows / macOS / Linux file
// names. Collapse whitespace. Trim. Never returns an empty string.
function _safeFilePart(s, fallback = "") {
  const clean = String(s || "")
    .replace(/[\\/:*?"<>|]+/g, "-")   // disallowed on Windows
    .replace(/[\u0000-\u001f]/g, "")  // control chars
    .replace(/\s+/g, " ")
    .trim();
  return clean || fallback;
}

// Build "<PartName> <Explanation> <SystemFileName>.<ext>" with safe chars.
// e.g. ("US5TD/D55", "Coloured Drawing", "5c8334c3.File.151412.pdf")
//      → "US5TD-D55 Coloured Drawing 5c8334c3.File.151412.pdf"
export function buildPartFileDownloadName(part, file) {
  const partName = _safeFilePart(
    part?.["Product Name"] || part?.["Display_Name"] || part?.ID || "part",
    "part",
  );
  const explanation = _safeFilePart(file?.explanation || "");
  const sysName = String(file?.file_name || "").trim();
  const m = /^(.*?)(\.[^.]+)?$/.exec(sysName);
  const stem = _safeFilePart(m?.[1] || sysName, "file");
  const ext = (m?.[2] || "").toLowerCase();
  const segments = [partName];
  if (explanation) segments.push(explanation);
  segments.push(stem);
  return segments.join(" ") + ext;
}

function iconFor(ext) {
  const e = String(ext || "").toLowerCase();
  if (IMG_EXT.includes(e)) return FileImage;
  if (PDF_EXT.includes(e)) return FilePdf;
  if (["xls", "xlsx", "csv"].includes(e)) return FileXls;
  if (["doc", "docx"].includes(e)) return FileDoc;
  return File;
}

// Lazy-resolve a Drive file on demand and kick off an action (download or preview).
async function resolveAndRun(name, kind, action, setResolving) {
  if (!name) return;
  setResolving(true);
  try {
    const data = await resolveDriveFile(name, kind);
    action(data);
  } catch (err) {
    toast.error(extractErr(err, "Failed to resolve file").message);
  } finally {
    setResolving(false);
  }
}

export default function PartFilesModal({ part, onClose, audience = "all", canUpload = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState("");
  const [uploadOk, setUploadOk] = useState("");
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const videoInputRef = useRef(null);

  useEffect(() => {
    if (!part?.ID) return;
    setLoading(true);
    fetchPartFiles(part.ID, audience)
      .then(setData)
      .catch((e) => setErr(e?.response?.data?.detail || "Failed to load files"))
      .finally(() => setLoading(false));
  }, [part?.ID, audience, refreshKey]);

  const handleUpload = async (file) => {
    if (!file || !part?.ID) return;
    setUploading(true);
    setUploadError("");
    setUploadOk("");
    setUploadProgress(0);
    try {
      await uploadPartFile(part.ID, file, {
        audience: "For everyone",
        explanation: "",
        file_type: file.type.startsWith("image/") ? "Picture"
                  : file.type.startsWith("video/") ? "Video"
                  : "File",
        onProgress: (pct) => setUploadProgress(pct),
      });
      setUploadOk(`Uploaded ${file.name}`);
      setRefreshKey((k) => k + 1);
      setTimeout(() => setUploadOk(""), 2500);
    } catch (e) {
      setUploadError(e?.response?.data?.detail || e?.message || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
      // Reset every input so the user can re-pick the same file if needed.
      [fileInputRef, cameraInputRef, videoInputRef].forEach((r) => {
        if (r.current) r.current.value = "";
      });
    }
  };

  const items = data?.items || [];
  const driveConfigured = data?.drive_configured;
  const baseUrl = data?.files_base_url;
  const hasResolver = Boolean(driveConfigured || baseUrl);

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 md:p-6" data-testid="part-files-overlay">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-3xl bg-white rounded-sm shadow-xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh]">
        <header className="px-4 md:px-6 py-3 border-b border-slate-200 flex items-center gap-3 shrink-0">
          <File size={18} weight="bold" className="text-blue-700" />
          <div className="flex-1 min-w-0">
            <h2 className="text-sm md:text-base font-bold font-['Chivo'] text-slate-900 truncate">
              Files for {part?.["Product Name"] || `Part #${part?.ID}`}
            </h2>
            {/* Subtitle: use Display_Name if it's different from the
                Product Name. Never expose the computed hash key. */}
            {part?.Display_Name && part.Display_Name !== part["Product Name"] && (
              <p className="text-[11px] text-slate-500 truncate" title={part.Display_Name}>
                {part.Display_Name}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="part-files-close"
            className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={18} />
          </button>
        </header>

        {!hasResolver && !loading && items.length > 0 && (
          <div className="mx-4 md:mx-6 mt-3 bg-amber-50 border border-amber-300 text-amber-900 text-xs rounded-sm p-3 flex items-start gap-2 shrink-0" data-testid="part-files-nobase">
            <Warning size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
            <div>
              No file resolver configured. An admin can connect Google Drive in <b>Admin Settings → Database → Google Drive</b>.
            </div>
          </div>
        )}

        <div className="p-4 md:p-6 overflow-y-auto flex-1">
          {canUpload && (
            <div data-testid="part-files-upload-panel" className="mb-4 rounded-sm border border-blue-200 bg-blue-50/40 p-3">
              <div className="flex items-center gap-2 mb-2">
                <Upload size={14} weight="bold" className="text-blue-700" />
                <p className="text-xs font-bold text-slate-900">Add a file to this part</p>
                {uploading && (
                  <span data-testid="part-files-upload-progress" className="ml-auto inline-flex items-center gap-1.5 text-[10px] font-bold text-blue-700">
                    <Spinner size={10} className="animate-spin" /> Uploading {uploadProgress > 0 ? `${uploadProgress}%` : "…"}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {/* Generic file picker (PDF / docs / pictures / videos). */}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  data-testid="part-files-upload-input"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
                <button
                  data-testid="part-files-upload-pick"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-bold rounded-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 disabled:opacity-50"
                >
                  <Upload size={11} weight="bold" /> Pick file
                </button>
                {/* Mobile camera capture — picture. `capture="environment"` opens the back camera. */}
                <input
                  ref={cameraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  data-testid="part-files-upload-camera-input"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
                <button
                  data-testid="part-files-upload-camera"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploading}
                  className="md:hidden inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-bold rounded-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 disabled:opacity-50"
                >
                  <Camera size={11} weight="bold" /> Take photo
                </button>
                {/* Mobile video capture. */}
                <input
                  ref={videoInputRef}
                  type="file"
                  accept="video/*"
                  capture="environment"
                  className="hidden"
                  data-testid="part-files-upload-video-input"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
                <button
                  data-testid="part-files-upload-video"
                  onClick={() => videoInputRef.current?.click()}
                  disabled={uploading}
                  className="md:hidden inline-flex items-center gap-1.5 h-8 px-3 text-[11px] font-bold rounded-sm bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 disabled:opacity-50"
                >
                  <Camera size={11} weight="bold" /> Record video
                </button>
                <span className="text-[10px] text-slate-500 self-center md:ml-auto">
                  Up to 100 MB · pdf, jpg, png, mov, mp4, doc, xls
                </span>
              </div>
              {uploadError && (
                <p data-testid="part-files-upload-error" className="mt-2 text-[11px] text-red-700 bg-red-50 border border-red-200 rounded-sm px-2 py-1">
                  {uploadError}
                </p>
              )}
              {uploadOk && (
                <p data-testid="part-files-upload-ok" className="mt-2 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1 inline-flex items-center gap-1">
                  <CheckCircle size={11} weight="bold" /> {uploadOk}
                </p>
              )}
            </div>
          )}
          {err && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm p-3">{err}</div>}
          {loading && <div className="text-sm text-slate-500">Loading files…</div>}
          {!loading && !err && items.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-10">No files registered for this part.</div>
          )}

          {!loading && items.length > 0 && (
            <ul className="space-y-2" data-testid="part-files-list">
              {items.map((f) => (
                <FileRow key={f.PFID} file={f} part={part} onOpenPreview={setPreview} driveConfigured={driveConfigured} />
              ))}
            </ul>
          )}
        </div>
      </div>

      {preview && <PreviewOverlay preview={preview} onClose={() => setPreview(null)} />}
    </div>,
    document.body
  );
}

function FileRow({ file, onOpenPreview, driveConfigured, part }) {
  const [resolving, setResolving] = useState(false);
  const [thumbUrl, setThumbUrl] = useState(THUMB_CACHE.get(file.file_name) || null);
  const [thumbLoading, setThumbLoading] = useState(false);
  const [hovering, setHovering] = useState(false);
  const anchorRef = useRef(null);
  const Icon = iconFor(file.ext);
  const ext = String(file.ext || "").toLowerCase();
  const isImg = IMG_EXT.includes(ext);
  const isPdf = PDF_EXT.includes(ext);
  const canPreview = driveConfigured && (isImg || isPdf);
  // Thumbnail is available only for known-previewable Drive files.
  const canHoverThumb = canPreview;
  // What the user sees and what they get when they hit Download.
  const displayLabel = (file.explanation || "").trim() || file.file_name || "—";
  const downloadName = buildPartFileDownloadName(part, file);

  // Kick off the thumbnail fetch once when the user intends to hover (200 ms debounce).
  const startHover = () => {
    setHovering(true);
    if (!canHoverThumb || thumbUrl || thumbLoading) return;
    clearTimeout(HOVER_DEBOUNCE);
    HOVER_DEBOUNCE = setTimeout(async () => {
      setThumbLoading(true);
      try {
        const r = await resolveDriveFile(file.file_name, "file");
        if (r?.thumbnail_url) {
          THUMB_CACHE.set(file.file_name, r.thumbnail_url);
          setThumbUrl(r.thumbnail_url);
        }
      } catch {
        // swallow — fall through to icon
      } finally {
        setThumbLoading(false);
      }
    }, 200);
  };
  const endHover = () => {
    clearTimeout(HOVER_DEBOUNCE);
    setHovering(false);
  };

  const runDownload = async (resolved) => {
    if (!resolved?.download_url) return;
    // Best effort: fetch the file as a Blob so we control the filename.
    // Google Drive's `uc?export=download` URL ignores the <a download>
    // attribute on cross-origin requests because of its own
    // Content-Disposition header — Blob URLs are same-origin, so the
    // download attribute is honoured.
    try {
      const r = await fetch(resolved.download_url, {
        mode: "cors",
        credentials: "omit",
      });
      if (!r.ok) throw new Error(`status ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      // CORS-blocked or network error — fall back to opening Drive
      // directly. The browser will use Drive's own filename here, but
      // at least the file still downloads.
      const a = document.createElement("a");
      a.href = resolved.download_url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = downloadName;     // best effort
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.message(
        "Browser blocked the renamed download.",
        { description: "File opened in a new tab with its original name." },
      );
    }
  };

  const runPreview = (resolved) => {
    onOpenPreview({ ...file, resolved });
  };

  return (
    <li data-testid={`part-file-${file.PFID}`}
      className="border border-slate-200 rounded-sm bg-white hover:border-blue-400 hover:shadow-sm transition-all">
      <div className="flex items-stretch gap-2 p-2 md:p-3">
        {/* Icon / hover-thumbnail anchor */}
        <div
          ref={anchorRef}
          onMouseEnter={startHover} onMouseLeave={endHover}
          onFocus={startHover} onBlur={endHover}
          tabIndex={canHoverThumb ? 0 : -1}
          data-testid={`part-file-hover-${file.PFID}`}
          className={`relative shrink-0 w-14 h-14 sm:w-16 sm:h-16 bg-slate-50 border border-slate-200 rounded-sm flex items-center justify-center ${canHoverThumb ? "cursor-help outline-none focus:ring-1 focus:ring-blue-500" : ""}`}
          title={canHoverThumb ? "Hover to peek at the file" : undefined}>
          <Icon size={28} weight="light" className="text-slate-400" />
          {canHoverThumb && (
            <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-blue-700 text-white border border-white shadow-sm flex items-center justify-center">
              <Eye size={9} weight="bold" />
            </span>
          )}

          {/* Popover: mini thumbnail while hovering */}
          {hovering && canHoverThumb && (
            <div data-testid={`part-file-thumb-popover-${file.PFID}`}
              className="absolute z-20 left-full top-1/2 -translate-y-1/2 ml-2 w-48 sm:w-56 bg-white border border-slate-200 shadow-lg rounded-sm p-1 pointer-events-none">
              {thumbLoading && !thumbUrl && (
                <div className="h-32 flex items-center justify-center text-slate-400 text-xs">
                  <Spinner size={16} className="animate-spin mr-1" /> Loading…
                </div>
              )}
              {thumbUrl && (
                <img src={thumbUrl} alt={file.file_name}
                  className="w-full h-auto max-h-48 object-contain bg-slate-900"
                  onError={(e) => { e.target.style.display = "none"; }} />
              )}
              {!thumbLoading && !thumbUrl && (
                <div className="h-20 flex items-center justify-center text-slate-400 text-xs px-2 text-center">
                  Thumbnail unavailable
                </div>
              )}
              <div className="text-[10px] text-slate-500 truncate mt-1 px-1">{file.file_name}</div>
            </div>
          )}
        </div>

        {/* Meta */}
        <div className="flex-1 min-w-0 flex flex-col gap-0.5">
          {/* Primary label: the human-readable explanation when we have
              one; otherwise fall back to the system filename. */}
          <div className="font-semibold text-xs md:text-sm text-slate-900 truncate"
               title={displayLabel}>
            {displayLabel}
          </div>
          {/* If the explanation IS the primary label, surface the
              system filename as a small caption so the user still
              knows what's stored on disk. */}
          {file.explanation && file.file_name && (
            <div className="text-[10px] font-mono text-slate-400 truncate"
                 title={file.file_name}>
              {file.file_name}
            </div>
          )}
          <div className="flex items-center gap-2 mt-auto flex-wrap">
            {file.file_type && (
              <span className="text-[9px] font-bold uppercase tracking-wider text-slate-500 bg-slate-100 border border-slate-200 px-1 py-0.5 rounded-sm">{file.file_type}</span>
            )}
            {file.file_for && (
              <span className="text-[9px] font-medium text-slate-500 truncate max-w-[100px]" title={file.file_for}>{file.file_for}</span>
            )}
            {file.date && <span className="text-[9px] text-slate-400 ml-auto">{String(file.date).slice(0, 10)}</span>}
          </div>
        </div>

        {/* Actions — only resolve+download when clicked */}
        <div className="flex flex-col sm:flex-row shrink-0 gap-1 items-stretch justify-center">
          {canPreview && (
            <button type="button" disabled={resolving}
              onClick={() => resolveAndRun(file.file_name, "file", runPreview, setResolving)}
              data-testid={`part-file-preview-${file.PFID}`}
              className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-slate-700 bg-white hover:bg-slate-100 border border-slate-200 px-2 py-1 rounded-sm min-w-[72px] disabled:opacity-50"
              title="Preview in overlay">
              {resolving ? <Spinner size={12} className="animate-spin" /> : <Eye size={12} weight="bold" />} Preview
            </button>
          )}
          <button type="button" disabled={resolving || !driveConfigured}
            onClick={() => resolveAndRun(file.file_name, "file", runDownload, setResolving)}
            data-testid={`part-file-download-${file.PFID}`}
            className="inline-flex items-center justify-center gap-1 text-[11px] font-bold text-white bg-blue-700 hover:bg-blue-800 px-2 py-1 rounded-sm min-w-[72px] disabled:opacity-50"
            title={driveConfigured ? "Download from Drive" : "Drive not configured"}>
            {resolving ? <Spinner size={12} className="animate-spin" /> : <Download size={12} weight="bold" />} Download
          </button>
        </div>
      </div>
    </li>
  );
}

function PreviewOverlay({ preview, onClose }) {
  const ext = String(preview.ext || "").toLowerCase();
  const isImg = IMG_EXT.includes(ext);
  const isPdf = PDF_EXT.includes(ext);
  const r = preview.resolved || {};
  return (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center p-2 md:p-8 z-10"
      onClick={onClose} data-testid="part-file-preview-overlay">
      <div className="relative bg-white rounded-sm max-w-5xl w-full h-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-200 shrink-0">
          <span className="text-xs font-semibold text-slate-700 truncate flex-1">{preview.file_name}</span>
          {r.download_url && (
            <a href={r.download_url} target="_blank" rel="noopener noreferrer"
              className="text-xs font-bold text-blue-700 hover:text-blue-900 flex items-center gap-1">
              <Download size={12} weight="bold" /> Download
            </a>
          )}
          <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-auto bg-slate-900 flex items-center justify-center">
          {isImg && r.thumbnail_url ? (
            <img src={r.thumbnail_url} alt={preview.file_name} className="max-w-full max-h-full object-contain" />
          ) : isPdf && r.embed_url ? (
            <iframe src={r.embed_url} title={preview.file_name} className="w-full h-full bg-white" />
          ) : (
            <div className="text-white text-sm">Preview not supported. Use Download.</div>
          )}
        </div>
      </div>
    </div>
  );
}
